import { Fragment, useState, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { useTranslation } from "react-i18next"
import { XIcon } from "@heroicons/react/solid"
import { Loader } from "../../components"
import { fetchCycleCountFilterOptions, createCycleCountAssignment } from "../../services/cycleCountServices"
import { BulkAssignFiltersStep } from "./BulkAssignFiltersStep"
import { BulkAssignPreviewStep } from "./BulkAssignPreviewStep"
import { BulkAssignOperatorStep } from "./BulkAssignOperatorStep"
import { BulkAssignConfirmStep } from "./BulkAssignConfirmStep"
import { localizeReviewError } from "./reviewErrors"

const T_PREFIX = 'cycle_counts.bulk_assign'

export const BulkAssignCycleCountDialog = ({
    open,
    warehouseId,
    onCancel,
    onSuccess,
}) => {
    const { i18n } = useTranslation()

    const [step, setStep] = useState('filters')
    const [filters, setFilters] = useState({ area: [], row: [], store_id: [] })
    const [filterOptions, setFilterOptions] = useState(null)
    const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
    const [previewData, setPreviewData] = useState(null)
    const [selectedOperators, setSelectedOperators] = useState([])
    const [busy, setBusy] = useState(false)

    // Load filter options when dialog opens
    useEffect(() => {
        if (open && warehouseId) {
            setFilterOptionsLoading(true)
            fetchCycleCountFilterOptions(warehouseId)
                .then((data) => {
                    setFilterOptions(data)
                    setFilterOptionsLoading(false)
                })
                .catch((err) => {
                    setFilterOptionsLoading(false)
                    const errMsg = err.response?.data?.error
                    window.alert(typeof errMsg === 'string' ? errMsg : errMsg?.message || i18n.t(`${T_PREFIX}.error`))
                })
        }
    }, [open, warehouseId, i18n])

    // Reset all state when dialog closes
    useEffect(() => {
        if (!open) {
            setStep('filters')
            setFilters({ area: [], row: [], store_id: [] })
            setPreviewData(null)
            setFilterOptions(null)
            setSelectedOperators([])
            setBusy(false)
        }
    }, [open])

    const handlePreviewNext = (data) => {
        setPreviewData(data)
        setStep('operators')
    }

    const handleOperatorStepNext = (operators) => {
        setSelectedOperators(operators)
        if (operators.length === 1) {
            handleCreateSingle(operators[0])
        } else {
            setStep('confirm')
        }
    }

    const handleCreateSingle = async (operator) => {
        setBusy(true)
        try {
            const cleanFilters = {}
            if (filters.area?.length) cleanFilters.area = filters.area
            if (filters.row?.length) cleanFilters.row = filters.row
            if (filters.store_id?.length) cleanFilters.store_id = filters.store_id

            const result = await createCycleCountAssignment({
                assigned_to_id: operator.id,
                warehouse_id: parseInt(warehouseId),
                filters: cleanFilters,
            })

            setBusy(false)
            onSuccess(result)
        } catch (error) {
            setBusy(false)
            handleError(error)
        }
    }

    const handleConfirmMulti = async () => {
        setBusy(true)
        try {
            const cleanFilters = {}
            if (filters.area?.length) cleanFilters.area = filters.area
            if (filters.row?.length) cleanFilters.row = filters.row
            if (filters.store_id?.length) cleanFilters.store_id = filters.store_id

            const result = await createCycleCountAssignment({
                assigned_to_ids: selectedOperators.map(op => op.id),
                warehouse_id: parseInt(warehouseId),
                filters: cleanFilters,
            })

            setBusy(false)
            onSuccess(result)
        } catch (error) {
            setBusy(false)
            handleError(error)
        }
    }

    const handleError = (error) => {
        const err = error.response?.data?.error
        const errorCode = error.response?.data?.error_code
        const rawMsg = typeof err === 'string' ? err : err?.message || i18n.t(`${T_PREFIX}.error`)
        window.alert(localizeReviewError(rawMsg, errorCode))
    }

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                onClose={() => {}}
            >
                <div className="flex items-center justify-center min-h-screen">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="relative bg-white rounded-lg shadow-xl sm:max-w-5xl sm:w-full p-6 text-gray-600 my-3 overflow-hidden max-h-[length:90vh]">
                            <Loader show={busy} />
                            <div className="absolute top-0 right-0 p-5 cursor-pointer" onClick={onCancel}>
                                <XIcon className="h-5 w-5" />
                            </div>
                            <Dialog.Title className="text-lg text-center mb-4">
                                {i18n.t(`${T_PREFIX}.title`)}
                            </Dialog.Title>

                            {filterOptionsLoading ? (
                                <div className="flex justify-center py-12">
                                    <Loader show={true} />
                                </div>
                            ) : step === 'filters' ? (
                                <BulkAssignFiltersStep
                                    filterOptions={filterOptions}
                                    filters={filters}
                                    onFiltersChange={setFilters}
                                    onPreview={() => setStep('preview')}
                                    onCancel={onCancel}
                                    loading={false}
                                />
                            ) : step === 'preview' ? (
                                <BulkAssignPreviewStep
                                    warehouseId={warehouseId}
                                    filters={filters}
                                    stores={filterOptions?.stores}
                                    onBack={() => setStep('filters')}
                                    onNext={handlePreviewNext}
                                />
                            ) : step === 'operators' ? (
                                <BulkAssignOperatorStep
                                    assignableCount={previewData?.summary?.assignable || 0}
                                    selectedOperators={selectedOperators}
                                    onOperatorsChange={setSelectedOperators}
                                    onNext={handleOperatorStepNext}
                                    onBack={() => setStep('preview')}
                                    busy={busy}
                                    warehouseId={warehouseId}
                                    role="ROLE_OPERATOR"
                                />
                            ) : step === 'confirm' ? (
                                <BulkAssignConfirmStep
                                    warehouseId={warehouseId}
                                    filters={filters}
                                    selectedOperators={selectedOperators}
                                    onConfirm={handleConfirmMulti}
                                    onBack={() => setStep('operators')}
                                    busy={busy}
                                />
                            ) : null}
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    )
}
